import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaUserTie,
  FaFileInvoice,
  FaWallet,
  FaListAlt,
  FaUniversity,
  FaMoneyBillWave,
  Fa500Px,
  FaPlus,
  FaEye,
  FaEyeSlash,
  FaBook,
  FaTasks,
  FaBoxOpen,
  FaLayerGroup,
} from "react-icons/fa";
import { MdClass } from "react-icons/md";
import { GiNotebook } from "react-icons/gi";
import api from "../../api";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showTotals, setShowTotals] = useState(false);

  const role = localStorage.getItem("role");
  const roleIsSuper = role === "super_admin";

  // Academic Year
  const [sessions, setSessions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  // --------------------------
  //  AUTH CHECK
  // --------------------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  // --------------------------
  //   FETCH DASHBOARD DATA
  // --------------------------
  const fetchData = async (yearId) => {
    setLoading(true);
    try {
      const res = await api.post("/get-data", {
        id: localStorage.getItem("id"),
        academic_year_id: yearId,
      });

      const resData = res.data?.data || {};

      setData({
        students: Number(resData.students || 0),
        teachers: Number(resData.teachers || 0),
        staff: Number(resData.staff || 0),
        pre_addmission_applicant: Number(resData.total_applicant || 0),
        bill: new Intl.NumberFormat("en-IN").format(resData.bill || 0),
        balance: new Intl.NumberFormat("en-IN").format(resData.balance || 0),
      });
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    }
    setLoading(false);
  };

  // --------------------------
  //   LOAD WHEN YEAR CHANGES
  // --------------------------
  useEffect(() => {
    if (selectedYear) fetchData(selectedYear);
  }, [selectedYear]);

  // --------------------------
  //   FETCH ACADEMIC SESSIONS
  // --------------------------
  const fetchSessions = async () => {
    try {
      const res = await api.get("school/academic_year/list");
      let data = res?.data?.data || [];

      data = data.map((d) => ({
        id: d.id,
        name: d.session_name,
        status: String(d.status).toLowerCase(),
      }));

      const storedId = localStorage.getItem("academic_year_id") || localStorage.getItem("academicYearId") || localStorage.getItem("selected_academic_year_id");
      const stored = data.find((x) => String(x.id) === String(storedId));
      const active = data.find((x) => x.status === "active");
      const selected = stored || active || data[0];
      setSelectedYear(selected?.id);
      if (selected?.id !== undefined && selected?.id !== null) localStorage.setItem("academic_year_id", String(selected.id));

      setSessions(data);
    } catch (err) {
      console.error("Session load failed", err);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  // --------------------------
  // STAT CARDS
  // --------------------------
  const statsCards = [
    {
      id: "students",
      label: "Total Students",
      icon: <FaUserGraduate />,
      color: "#1976D2",
      valueKey: "students",
      routeOnCard: "/admin/view-student",
      routeOnButton: "/admin/add-student",
      buttonLabel: "Add Student",
      buttonIcon: <FaPlus />,
    },
    {
      id: "teachers",
      label: "Total Teachers",
      icon: <FaChalkboardTeacher />,
      color: "#2ECC71",
      valueKey: "teachers",
      routeOnCard: "/admin/teacher-add",
      routeOnButton: "/admin/teacher-add",
      buttonLabel: "Add Teacher",
      buttonIcon: <FaPlus />,
    },
    {
      id: "staff",
      label: "Total Staff",
      icon: <FaUserTie />,
      color: "#F39C12",
      valueKey: "staff",
      routeOnCard: "/admin/dashboard",
      routeOnButton: "/admin/dashboard",
      buttonLabel: "Add Staff",
      buttonIcon: <FaPlus />,
    },
    {
      id: "billing",
      label: "Total Billing",
      icon: <FaFileInvoice />,
      color: "#6C63FF",
      valueKey: "bill",
      routeOnCard: roleIsSuper ? "/admin/all-billing" : "/admin/student-billing",
      routeOnButton: "/admin/student-billing",
      buttonLabel: "New Bill",
      buttonIcon: <FaPlus />,
    },
    {
      id: "wallet",
      label: roleIsSuper ? "Admin Wallet" : "Wallet",
      icon: <FaWallet />,
      color: "#009688",
      valueKey: "balance",
      routeOnCard: roleIsSuper ? "/admin/wallet" : "/admin/staff/wallet",
      routeOnButton: "/admin/wallet-history",
      buttonLabel: "Wallet History",
      buttonIcon: <FaEye />,
    },
    {
      id: "pre_addmission_applicant",
      label: "Total Applicant",
      icon: <FaListAlt />,
      color: "#34495E",
      valueKey: "pre_addmission_applicant",
      routeOnCard: "/admin/applicant/view",
      routeOnButton: "/admin/applicant/view",
      buttonLabel: "View",
      buttonIcon: <FaEye />,
    },
  ];

  const readValue = (key) => {
    if (!showTotals) return key === "bill" || key === "balance" ? "₹ ••••••" : "••••";
    if (!data) return "-";
    if (key === "bill" || key === "balance") return `₹ ${data[key]}`;
    return data[key] ?? 0;
  };

  // --------------------------
  // WORKFLOW GROUP
  // --------------------------
  const workflowGroups = [
    {
      title: "Student Registration Process",
      steps: [
        { label: "Add Class & Section", icon: <MdClass />, color: "#1E88E5", to: "/admin/class-section/manage" },
        { label: "Add Fees Type", icon: <FaMoneyBillWave />, color: "#00796B", to: "/admin/fees_structure/list" },
        { label: "Add Student", icon: <FaUserGraduate />, color: "#0288D1", to: "/admin/add-student" },
        { label: "View Student", icon: <GiNotebook />, color: "#3949AB", to: "/admin/view-student" },
      ],
    },
    {
      title: "Teacher Setup Process",
      steps: [
        { label: "Add Department", icon: <FaUniversity />, color: "#2E86C1", to: "/admin/department-add" },
        { label: "Add Teacher", icon: <FaChalkboardTeacher />, color: "#27AE60", to: "/admin/teacher-add" },
        { label: "View Department", icon: <FaUniversity />, color: "#2E86C1", to: "/admin/department-add" },
        { label: "View Teacher", icon: <FaChalkboardTeacher />, color: "#27AE60", to: "/admin/teacher-add" },
      ],
    },
    {
      title: "Subject Assignment Process",
      steps: [
        { label: "Add Subject", icon: <FaBook />, color: "#16A085", to: "/admin/subject-add" },
        { label: "Assign Subject", icon: <FaTasks />, color: "#2ECC71", to: "/admin/subject-class/assign" },
        { label: "Assign Subject To Teacher", icon: <FaUserTie />, color: "#C0392B", to: "/admin/teacher-subject/assign" },
        { label: "View Assign Subject To Teacher", icon: <FaUserTie />, color: "#C0392B", to: "/admin/teacher-subject/assign" },
      ],
    },
    {
      title: "Product Management Process",
      steps: [
        { label: "Add Category", icon: <FaLayerGroup />, color: "#F57C00", to: "/admin/product-category" },
        { label: "Add Sub Category", icon: <FaBoxOpen />, color: "#EF6C00", to: "/admin/product-sub-category" },
        { label: "Add Product", icon: <FaBoxOpen />, color: "#8E24AA", to: "/admin/product-bundle/create" },
        { label: "View Stock", icon: <FaListAlt />, color: "#5E35B1", to: "/admin/product-bundle/list" },
      ],
    },
    {
      title: "Expense Management Process",
      steps: [
        { label: "Add Expense Category", icon: <FaListAlt />, color: "#E53935", to: "/admin/expense/category" },
        { label: "Add Expense Sub Category", icon: <FaLayerGroup />, color: "#D81B60", to: "/admin/expense/sub-category" },
        { label: "Add Vendor", icon: <FaUserTie />, color: "#8E24AA", to: "/admin/vendor/list" },
        { label: "Add Expense", icon: <Fa500Px />, color: "#6A1B9A", to: "/admin/expense/add" },
      ],
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 3 },
        minHeight: "100vh",
        background: "transparent",
      }}
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 260 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Header + Dropdown */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              gap: 2,
              mb: 3.5,
              p: { xs: 2.25, md: 3 },
              borderRadius: 3,
              position: "relative",
              overflow: "hidden",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(240,249,255,0.96))",
              border: "1px solid rgba(37,99,235,0.12)",
              boxShadow: "0 18px 44px rgba(23,32,51,0.11)",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at 12% 0%, rgba(37,99,235,0.1), transparent 28%), radial-gradient(circle at 88% 0%, rgba(22,163,74,0.1), transparent 26%)",
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 2,
                background: "#d7e3f1",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: 24, md: 30 },
                    color: "#102a43",
                    lineHeight: 1.1,
                    textShadow: "0 1px 0 rgba(255,255,255,0.7)",
                  }}
                >
                  Dashboard Overview
                </Typography>
                <IconButton
                  type="button"
                  onClick={() => setShowTotals((prev) => !prev)}
                  aria-label={showTotals ? "Hide dashboard totals" : "Show dashboard totals"}
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "14px",
                    color: showTotals ? "#2563eb" : "#64748b",
                    background: "#ffffff",
                    border: "1px solid rgba(37,99,235,0.16)",
                    boxShadow: "0 12px 26px rgba(37,99,235,0.13)",
                    "& svg": {
                      fontSize: 22,
                    },
                    "&:hover": {
                      background: "#eff6ff",
                      color: "#0f766e",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {showTotals ? <FaEye /> : <FaEyeSlash />}
                </IconButton>
              </Box>
              <Typography
                sx={{
                  mt: 0.75,
                  color: "#607086",
                  fontWeight: 650,
                  fontSize: { xs: 13, md: 14 },
                }}
              >
                Live school summary, accounts and daily workflow shortcuts
              </Typography>
            </Box>

            {/* BEAUTIFUL DROPDOWN */}
            <TextField
              select
              label="Academic Year"
              size="small"
              value={selectedYear}
              onChange={(e) => {
                const nextYearId = e.target.value;
                setSelectedYear(nextYearId);
                localStorage.setItem("academic_year_id", String(nextYearId));
                window.dispatchEvent(new CustomEvent("academicYearChanged", { detail: { academicYearId: String(nextYearId) } }));
              }}
              SelectProps={{ native: true }}
              sx={{
                width: { xs: "100%", md: 270 },
                position: "relative",
                zIndex: 1,
                background: "rgba(255,255,255,0.96)",
                borderRadius: 2,
                boxShadow: "0 16px 34px rgba(0,0,0,0.18)",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  fontWeight: 800,
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 800,
                },
              }}
            >
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.status === "active" ? "(Active)" : ""}
                </option>
              ))}
            </TextField>
          </Box>

          {/* Stats */}
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {statsCards.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} key={card.id}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      p: { xs: 2, md: 2.25 },
                      borderRadius: 3,
                      minHeight: 126,
                      backdropFilter: "blur(10px)",
                      background:
                        "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(248,251,255,0.9))",
                      border: "1px solid rgba(78,102,135,0.16)",
                      boxShadow: "0 14px 34px rgba(23,32,51,0.1)",
                      transition: "all .25s ease",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${card.color}, ${card.color}88)`,
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        width: 140,
                        height: 140,
                        right: -52,
                        bottom: -70,
                        borderRadius: "50%",
                        background: `${card.color}14`,
                        transition: "transform .25s ease, opacity .25s ease",
                      },
                      "&:hover": {
                        transform: "translateY(-7px)",
                        boxShadow: `0 22px 48px rgba(23,32,51,0.14), 0 0 0 1px ${card.color}24`,
                        "&::after": {
                          transform: "scale(1.18)",
                          opacity: 0.95,
                        },
                      },
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(card.routeOnCard)}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2.25,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        background: `linear-gradient(135deg, ${card.color}24, ${card.color}44)`,
                        border: `1px solid ${card.color}22`,
                        boxShadow: `0 12px 24px ${card.color}20`,
                      }}
                    >
                      <Box sx={{ color: card.color, fontSize: 28 }}>{card.icon}</Box>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0, px: { xs: 0.5, md: 1.5 } }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#607086" }}>
                        {card.label}
                      </Typography>
                      <Typography
                        noWrap
                        sx={{
                          mt: 0.25,
                          fontWeight: 950,
                          fontSize: { xs: 23, md: 26 },
                          color: "#172033",
                          lineHeight: 1.1,
                          letterSpacing: showTotals ? 0 : 1.5,
                        }}
                      >
                        {readValue(card.valueKey)}
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(card.routeOnButton);
                      }}
                      startIcon={card.buttonIcon}
                      sx={{
                        background: `linear-gradient(90deg, ${card.color}, ${card.color}cc)`,
                        color: "white",
                        fontWeight: 850,
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.8,
                        boxShadow: `0 12px 24px ${card.color}35`,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {card.buttonLabel}
                    </Button>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 5, opacity: 0.22 }} />

          {/* ⚙️ Workflow Groups */}
          {workflowGroups.map((group, gIndex) => (
            <Box key={gIndex} sx={{ mb: 5.5 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2.2,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  fontWeight: 900,
                  color: "#102a43",
                  "&::before": {
                    content: '""',
                    width: 8,
                    height: 28,
                    borderRadius: 999,
                    background: "linear-gradient(180deg, #2563eb, #16a34a)",
                    display: "inline-block",
                  },
                }}
              >
                {group.title}
              </Typography>
              <Grid container spacing={{ xs: 2, md: 3 }}>
                {group.steps.map((step, index) => (
                  <Grid item xs={12} sm={6} md={3} key={step.label}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                    <Paper
                        elevation={0}
                      sx={{
                          position: "relative",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: 154,
                          p: 3,
                          borderRadius: 3,
                          backdropFilter: "blur(10px)",
                          background:
                            "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(248,251,255,0.9))",
                          border: "1px solid rgba(78,102,135,0.16)",
                          textAlign: "center",
                          transition: "all .25s ease",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: "auto 20px 0 20px",
                            height: 3,
                            borderRadius: 999,
                            background: `linear-gradient(90deg, transparent, ${step.color}, transparent)`,
                            opacity: 0,
                            transition: "opacity .22s ease",
                          },
                        "&:hover": {
                          transform: "translateY(-7px)",
                            boxShadow: "0 18px 42px rgba(23,32,51,0.14)",
                            borderColor: `${step.color}35`,
                            "&::before": {
                              opacity: 1,
                            },
                        },
                          cursor: "pointer",
                      }}
                      onClick={() => navigate(step.to)}
                    >
                      <Box
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: "50%",
                          display: "grid",
                          placeItems: "center",
                            background: `linear-gradient(135deg, ${step.color}24, ${step.color}44)`,
                            border: `1px solid ${step.color}24`,
                            boxShadow: `0 14px 30px ${step.color}20`,
                          mb: 1.5,
                        }}
                      >
                          <Box sx={{ color: step.color, fontSize: 30 }}>{step.icon}</Box>
                        </Box>
                        <Typography sx={{ fontWeight: 850, fontSize: 15, color: "#24364a" }}>
                        {step.label}
                      </Typography>
                    </Paper>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};

export default Dashboard;




